import React, { useState } from "react";
import styled from "styled-components";
import { Bio } from "../../data/constants";
import { motion } from "framer-motion";
import {
  headContainerAnimation,
  headContentAnimation,
  headTextAnimation,
} from "../../utils/motion";

const HeroContainer = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  padding: 80px 30px;
  z-index: 1;
  background-color: transparent;

  @media (max-width: 960px) {
    padding: 40px 16px;
  }

  @media (max-width: 640px) {
    padding: 32px 16px;
  }
`;

const HeroInnerContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1100px;
  gap: 4rem;

  @media (max-width: 960px) {
    flex-direction: column;
    gap: 2.5rem;
  }
`;

const HeroLeftContainer = styled.div`
  width: 100%;
  flex: 1;
  order: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 960px) {
    order: 1;
    align-items: center;
    text-align: center;
  }
`;

const HeroRightContainer = styled.div`
  width: 100%;
  flex: 1.2;
  order: 2;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 960px) {
    order: 2;
    width: 100%;
    max-width: 500px;
  }
`;

const Title = styled.h1`
  font-weight: 700;
  font-size: 3rem;
  color: ${({ theme }) => theme.white};
  line-height: 1.2;
  letter-spacing: -0.03em;
  margin-bottom: 0.25rem;

  @media (max-width: 960px) {
    font-size: 2.5rem;
  }

  @media (max-width: 640px) {
    font-size: 2.25rem;
  }
`;

const SubTitle = styled.div`
  font-size: 1.15rem;
  color: ${({ theme }) => theme.primary};
  font-weight: 500;
  margin-bottom: 1.5rem;
  font-family: 'JetBrains Mono', monospace;

  @media (max-width: 640px) {
    font-size: 1rem;
  }
`;



const BioText = styled.p`
  color: ${({ theme }) => theme.text_secondary};
  font-size: 1.05rem;
  line-height: 1.6;
  margin-bottom: 2.5rem;
  max-width: 90%;

  @media (max-width: 960px) {
    max-width: 100%;
  }
`;

const LinksContainer = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 960px) {
    justify-content: center;
  }
`;

const Button = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;
  font-family: inherit;
`;

const PrimaryButton = styled(Button)`
  background-color: ${({ theme }) => theme.primary};
  color: #121212;
  border: 1px solid ${({ theme }) => theme.primary};

  &:hover {
    background-color: #9ccc65;
    border-color: #9ccc65;
    transform: translateY(-2px);
  }
`;

const SecondaryButton = styled(Button)`
  background-color: transparent;
  color: ${({ theme }) => theme.text_primary};
  border: 1px solid #444;

  &:hover {
    border-color: ${({ theme }) => theme.text_primary};
    background-color: rgba(255, 255, 255, 0.05);
    transform: translateY(-2px);
  }
`;

// API Explorer Styles
const ApiExplorerContainer = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.card};
  border: 1px solid #333;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
  font-family: 'JetBrains Mono', monospace;
`;

const WindowControls = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  background: #252526;
  border-bottom: 1px solid #333;
`;

const Dot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => props.$color};
`;

const ApiExplorerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.8rem 1rem;
  background: #1e1e1e;
  border-bottom: 1px solid #333;
`;

const RequestBar = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 0.5rem;
  padding: 0.8rem 1rem;
  background: #252526;
  border-bottom: 1px solid #333;

  @media (max-width: 520px) {
    grid-template-columns: auto minmax(0, 1fr);
  }
`;

const HttpMethodSelect = styled.select`
  font-size: 0.8rem;
  font-weight: 700;
  color: ${({ theme }) => theme.primary};
  background: #1a1a1a;
  border: 1px solid #444;
  border-radius: 4px;
  padding: 0.4rem 1.6rem 0.4rem 0.6rem;
  outline: none;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
  flex-shrink: 0;
  min-height: 2.35rem;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%238bc34a' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 0.4rem center;
  background-size: 14px;
  transition: all 0.2s ease;
  font-family: inherit;

  &:focus {
    border-color: ${({ theme }) => theme.primary};
  }
`;

const EndpointSelect = styled.select`
  width: 100%;
  background: #1e1e1e;
  color: #e0e0e0;
  border: 1px solid #444;
  border-radius: 4px;
  padding: 0.5rem 0.7rem;
  font-family: inherit;
  font-size: 0.85rem;
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    border-color: ${({ theme }) => theme.primary};
  }

  @media (max-width: 520px) {
    grid-column: 1 / -1;
  }
`;

const ExecuteBtn = styled.button`
  background: transparent;
  color: ${({ theme }) => theme.primary};
  border: 1px solid ${({ theme }) => theme.primary};
  border-radius: 4px;
  padding: 0.4rem 0.8rem;
  font-size: 0.8rem;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s;
  min-height: 2.35rem;

  &:hover:not(:disabled) {
    background: rgba(139, 195, 74, 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 520px) {
    justify-self: end;
  }
`;

const ResponsePanel = styled.div`
  background: #121212;
  padding: 1rem;
  height: 350px;
  overflow-y: auto;
  font-size: 0.8rem;
  text-align: left;
`;

const ResHint = styled.div`
  color: #888;
`;

const BlinkingCursor = styled.span`
  display: inline-block;
  width: 8px;
  height: 15px;
  background: ${({ theme }) => theme.primary};
  vertical-align: middle;
  margin-left: 4px;
  animation: blink 0.8s step-end infinite;

  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }
`;

const ResTopBar = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: 1px dashed #333;
  padding-bottom: 0.5rem;
  margin-bottom: 0.5rem;
`;

const ResOk = styled.span`
  color: ${({ theme }) => theme.primary};
  font-weight: 700;
`;

const ResHdr = styled.div`
  font-size: 0.75rem;
  color: #888;
`;

const ResHdrKey = styled.span`
  color: #b3b3b3;
`;

const ResBody = styled.div`
  margin-top: 1rem;
  white-space: pre-wrap;
`;

// Syntax highlighting styles
const KeySpan = styled.span`
  color: #9cdcfe;
`;

const StringSpan = styled.span`
  color: #ce9178;
`;

const NumberSpan = styled.span`
  color: #b5cea8;
`;

const BooleanSpan = styled.span`
  color: #569cd6;
`;

const BraceSpan = styled.span`
  color: #d4d4d4;
`;

const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};

const ENDPOINTS = {
  profile: {
    ms: 42,
    headers: [
      ['Content-Type', 'application/json'],
      ['X-Powered-By', 'Node.js/Express']
    ],
    body: {
      id: "DEV-7649",
      name: "Lucky Paswan",
      role: "Software Developer",
      specialization: [
        "Full Stack Development",
        "Backend Engineering",
        "MERN Stack"
      ],
      location: "Bhopal, India",
      education: {
        degree: "Bachelor of Technology",
        institution: "Sagar institute of Science Technology and Research",
        cgpa: "7.49",
        status: "In Progress"
      },
      contact: {
        email: "luckypaswan745@gmail.com",
        github: "https://github.com/lucky7649",
        linkedin: "https://www.linkedin.com/in/lucky-paswan-9086b9215"
      },
      currentFocus: [
        "React.js",
        "Node.js",
        "MongoDB",
        "Django REST Framework"
      ]
    }
  },
  skills: {
    ms: 35,
    headers: [['Content-Type', 'application/json']],
    body: {
      frontend: ["React Js", "Redux", "HTML", "CSS", "JavaScript", "Tailwind CSS"],
      backend: ["Node Js", "Express Js", "Django", "Django REST Framework", "FastAPI"],
      databases: ["MySQL", "MongoDB", "PostgreSQL"],
      languages: ["C", "C++", "Python", "Java"]
    }
  },
  projects: {
    ms: 68,
    headers: [['Content-Type', 'application/json']],
    body: [
      {
        name: "Virtual Herbal Garden",
        type: "Full-stack Web App",
        stack: ["React Js", "Mongo DB", "Node Js", "Express Js", "Tailwind"],
        description: "Interactive garden web application to explore plants and benefits."
      },
      {
        name: "Health Care Management System",
        type: "Full-stack Web App",
        stack: ["React Js", "MongoDb", "Node Js", "Express Js", "Tailwind"],
        description: "Secure digital platform for patients, appointments, and history."
      }
    ]
  }
};

const Hero = () => {
  const [method, setMethod] = useState("GET");
  const [endpoint, setEndpoint] = useState("profile");
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);

  const handleExecute = async () => {
    setLoading(true);
    setResponse(null);

    const config = ENDPOINTS[endpoint];
    const delay = config.ms + Math.floor(Math.random() * 25);
    
    await new Promise((resolve) => setTimeout(resolve, delay));

    let statusCode = "200 OK";
    let body = config.body;

    if (method === "POST") {
      statusCode = "201 Created";
      body = {
        status: 201,
        message: "Resource initialized.",
        request_id: generateUUID()
      };
    }

    setResponse({
      statusCode,
      delay,
      headers: config.headers,
      body
    });
    setLoading(false);
  };

  const renderHighlightedJson = (obj) => {
    const jsonString = JSON.stringify(obj, null, 2);
    
    // Simple tokenizer for basic JSON highlighting
    const lines = jsonString.split("\n");
    return lines.map((line, idx) => {
      // Check for keys
      const keyRegex = /^(\s*)"([^"]+)"(\s*:\s*)/;
      const keyMatch = line.match(keyRegex);
      
      if (keyMatch) {
        const spacing = keyMatch[1];
        const key = keyMatch[2];
        const colon = keyMatch[3];
        const rest = line.substring(keyMatch[0].length);
        
        let valSpan = <span>{rest}</span>;
        if (rest.trim().startsWith('"')) {
          valSpan = <StringSpan>{rest}</StringSpan>;
        } else if (/^\d+/.test(rest.trim())) {
          valSpan = <NumberSpan>{rest}</NumberSpan>;
        } else if (/^(true|false)/.test(rest.trim())) {
          valSpan = <BooleanSpan>{rest}</BooleanSpan>;
        } else if (/^null/.test(rest.trim())) {
          valSpan = <BooleanSpan>{rest}</BooleanSpan>;
        }

        return (
          <div key={idx}>
            {spacing}
            <KeySpan>"{key}"</KeySpan>
            {colon}
            {valSpan}
          </div>
        );
      }

      if (line.trim() === "{" || line.trim() === "}" || line.trim() === "[" || line.trim() === "]") {
        return <div key={idx}><BraceSpan>{line}</BraceSpan></div>;
      }
      
      if (line.trim().startsWith('"')) {
        return <div key={idx}><StringSpan>{line}</StringSpan></div>;
      }

      return <div key={idx}>{line}</div>;
    });
  };

  return (
    <div id="About">
      <HeroContainer>
        <motion.div {...headContainerAnimation}>
          <HeroInnerContainer>
            <HeroLeftContainer>
              <motion.div {...headTextAnimation}>
                <Title>Hi, I am <br /> {Bio.name}</Title>
                <SubTitle>Software Developer</SubTitle>
              </motion.div>
              
              <motion.div {...headContentAnimation}>
                <BioText>{Bio.description}</BioText>
              </motion.div>

              <LinksContainer>
                <PrimaryButton href={Bio.resume} target="_blank">
                  View CV
                </PrimaryButton>
                <SecondaryButton href={Bio.github} target="_blank">
                  GitHub
                </SecondaryButton>
                <SecondaryButton href={Bio.linkedin} target="_blank">
                  LinkedIn
                </SecondaryButton>
              </LinksContainer>
            </HeroLeftContainer>

            <HeroRightContainer>
              <ApiExplorerContainer>
                <WindowControls>
                  <Dot $color="#ff5f56" />
                  <Dot $color="#ffbd2e" />
                  <Dot $color="#27c93f" />
                </WindowControls>
                
                <ApiExplorerHeader>
                  <svg fill="none" height="16" stroke="#888" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" viewBox="0 0 24 24" width="16">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                  </svg>
                  <span style={{ fontSize: "0.85rem", color: "#e0e0e0", letterSpacing: "0.02em" }}>
                    PortfolioController.java
                  </span>
                </ApiExplorerHeader>

                <RequestBar>
                  <HttpMethodSelect value={method} onChange={(e) => setMethod(e.target.value)}>
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                  </HttpMethodSelect>

                  <EndpointSelect value={endpoint} onChange={(e) => setEndpoint(e.target.value)}>
                    <option value="profile">lucky7649.com/api/cv/profile</option>
                    <option value="skills">lucky7649.com/api/cv/skills</option>
                    <option value="projects">lucky7649.com/api/cv/projects</option>
                  </EndpointSelect>

                  <ExecuteBtn onClick={handleExecute} disabled={loading} id="executeBtn">
                    {loading ? "Executing..." : "Execute"}
                  </ExecuteBtn>
                </RequestBar>

                <ResponsePanel>
                  {loading && (
                    <ResHint>
                      <span style={{ color: "#8bc34a" }}>$</span> Sending API request...
                      <BlinkingCursor />
                    </ResHint>
                  )}
                  
                  {!loading && !response && (
                    <ResHint>
                      <span style={{ color: "#8bc34a" }}>$</span> Select an endpoint and hit execute to query my API...
                      <BlinkingCursor />
                    </ResHint>
                  )}

                  {!loading && response && (
                    <>
                      <ResTopBar>
                        <div>
                          HTTP/1.1 <ResOk>{response.statusCode}</ResOk>{" "}
                          <span style={{ color: "#666", marginLeft: "1rem" }}>{response.delay}ms</span>
                        </div>
                      </ResTopBar>
                      {response.headers.map(([k, v], i) => (
                        <ResHdr key={i}>
                          <ResHdrKey>{k}</ResHdrKey>: {v}
                        </ResHdr>
                      ))}
                      <ResBody>
                        {renderHighlightedJson(response.body)}
                      </ResBody>
                    </>
                  )}
                </ResponsePanel>
              </ApiExplorerContainer>
            </HeroRightContainer>
          </HeroInnerContainer>
        </motion.div>
      </HeroContainer>
    </div>
  );
};

export default Hero;
